import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraActionList, SpecraBadge, SpecraButton, SpecraEmptyState, SpecraSectionHeader, SpecraSegmentedControl, SpecraText, specraButtonVariants } from '@mono/ui-components';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { ATTENTION_CAP } from './attention';
export function AttentionQueue({ title, rule, testids, loading, empty, tabs, tab, onTabChange, rows, hiddenNote, footer }) {
    // "Show more" opens one tab; changing tab closes it.
    const [openTab, setOpenTab] = useState(null);
    const open = openTab === tab;
    const shown = open ? rows : rows.slice(0, ATTENTION_CAP);
    const hidden = rows.slice(shown.length);
    const items = shown.map((row, i)=>{
        const v = row.verb;
        const variant = v?.variant ?? (row.primary ?? i === 0 ? 'default' : 'secondary');
        const verbClass = specraButtonVariants({
            variant,
            size: 'sm'
        });
        return {
            id: row.id,
            label: /*#__PURE__*/ _jsxs("span", {
                className: "flex min-w-0 items-center gap-[var(--space-3)]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "w-[var(--component-attention-badge-width)] shrink-0",
                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                            "data-kind": row.kind,
                            variant: row.badge.tone,
                            children: row.badge.label
                        })
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "min-w-0 truncate",
                        children: row.what
                    })
                ]
            }),
            description: /*#__PURE__*/ _jsx("span", {
                className: "block truncate pl-[calc(var(--component-attention-badge-width)+var(--space-3))]",
                children: row.where
            }),
            meta: /*#__PURE__*/ _jsxs("span", {
                className: "block text-right",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: row.when.urgent ? 'block text-danger' : 'block',
                        children: row.when.date
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "block",
                        children: row.when.rel
                    })
                ]
            }),
            actions: !v && !row.extra ? undefined : /*#__PURE__*/ _jsxs("span", {
                className: "flex items-center gap-[var(--space-2)]",
                children: [
                    row.extra,
                    !v ? null : v.href ? /*#__PURE__*/ _jsx(Link, {
                        "aria-label": `${v.label}: ${row.what}`,
                        className: verbClass,
                        "data-testid": v.testId ?? testids.verb,
                        to: v.href,
                        children: v.label
                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                        "aria-label": `${v.label}: ${row.what}`,
                        "data-testid": v.testId ?? testids.verb,
                        onClick: v.onClick,
                        size: "sm",
                        variant: variant,
                        children: v.label
                    })
                ]
            })
        };
    });
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "data-testid": testids.root,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-baseline justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        headingAs: "h2",
                        title: title,
                        variant: "group"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: rule
                    })
                ]
            }),
            loading ? /*#__PURE__*/ _jsx("div", {
                "aria-hidden": "true",
                className: "h-32 animate-pulse rounded-[var(--radius-lg)] bg-muted"
            }) : empty ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                "data-testid": testids.empty,
                description: empty.description,
                framed: true,
                title: empty.title
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "Show",
                        onValueChange: (v)=>onTabChange(v),
                        options: tabs.map((t)=>({
                                value: t.value,
                                label: t.label,
                                count: t.count,
                                testId: testids.tab
                            })),
                        size: "sm",
                        value: tab
                    }),
                    /*#__PURE__*/ _jsx(SpecraActionList, {
                        "data-testid": testids.list,
                        getItemTestId: ()=>testids.row,
                        items: items,
                        renderLink: renderRouterLink
                    }),
                    hidden.length > 0 || footer ? /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-[var(--space-3)]",
                        children: [
                            hidden.length > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraButton, {
                                        className: "text-[length:var(--fs-ui-body)]",
                                        "data-testid": testids.more,
                                        onClick: ()=>setOpenTab(tab),
                                        size: "inline",
                                        variant: "link",
                                        children: [
                                            "Show ",
                                            hidden.length.toLocaleString(),
                                            " more"
                                        ]
                                    }),
                                    hiddenNote ? /*#__PURE__*/ _jsx(SpecraText, {
                                        size: "meta",
                                        tone: "muted",
                                        children: hiddenNote(hidden)
                                    }) : null
                                ]
                            }) : null,
                            footer
                        ]
                    }) : null
                ]
            })
        ]
    });
}
